// @givmo/org-console — the REQUEST dialog: "Request access to <App> as <Member>" (shell contract v3,
// T5.1, R8/R9).
//
// A request asks for a ROLE in a module, never bare "access" (R8), so the dialog's first control is
// the tier — pre-set to the module's Member role from a tile, and to Owner ONLY when the row that
// opened it needs a page whose string only the Owner catalog carries (then the dialog says so and
// offers no Member option — mockup requirement 9). An explanation is optional. The sentence at the
// bottom tells the truth about where the request goes (R9): to the app's Owners AND the
// organization's Global Admins — never "…from global admin".
//
// It owns its own write (the create operation) so every door that opens it — a tile, a sidebar row,
// the direct-link gate, the declined popover — gets the same request and the same refusals:
//   * 409 — the caller already holds the module (a Global Admin holds every one). The tile that
//     opened this should never have; it is stated rather than swallowed.
//   * 422 — the class does not carry the module; the same honesty.
//   * anything else — the generic sentence, and the dialog stays open to try again.
//
// DATA CONTRACT: the `org-request-*` test-id family.

import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  ErrorMessage,
  RadioTile,
  RadioTileGroup,
  Textarea,
  testIdProp,
} from '@givmo/design-system';
import { isGivmoError } from '@givmo/api-client';
import type { OrgConsoleConfig } from './org-console-config';
import {
  ROLE_MEMBER,
  ROLE_OWNER,
  moduleMemberDescription,
  moduleOwnerDescription,
  moduleTierLabel,
  type OrgTier,
} from './org-tiers';
import { useCreateOrgAccessRequest } from './use-org-console';
import type { OrgAccessRequestRow } from './wire-types';

const PREFIX = 'org-request';

/** The sentence under the form — pinned copy (D-CPO-31; cased per mockup requirement 8). */
export const REQUEST_GOES_TO_SENTENCE = `Your request goes to this app's ${moduleTierLabel(ROLE_OWNER)}s and your organization's Global Admins.`;

export interface RequestAccessDialogProps {
  config: OrgConsoleConfig;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The module being asked for. `null` while closed. */
  module: string | null;
  /**
   * The tier the dialog pre-selects — `Member` unless a caller says otherwise; both tiers are always
   * offered. (The Owner-only form, for a greyed Team row, retired with Org Machinery OM-29: that row
   * is absent for anyone who cannot use it, so no door asks for Owner alone.)
   */
  defaultRole?: OrgTier;
  /** Called after the request lands, with the row the server answered. */
  onSent?: (row: OrgAccessRequestRow | null) => void;
}

function refusalCopy(error: unknown, appLabel: string): string {
  if (isGivmoError(error)) {
    if (error.status === 409) return `You already have access to ${appLabel}.`;
    if (error.status === 422) return `${appLabel} is not offered to your organization.`;
  }
  return 'We couldn’t send your request. Nothing was saved — please try again.';
}

export function RequestAccessDialog({
  config,
  open,
  onOpenChange,
  module,
  defaultRole = ROLE_MEMBER,
  onSent,
}: RequestAccessDialogProps) {
  const initialRole: OrgTier = defaultRole;
  const [role, setRole] = useState<string>(initialRole);
  const [explanation, setExplanation] = useState('');
  const create = useCreateOrgAccessRequest(config.orgRef);
  const appLabel = module === null ? '' : config.vocab.moduleLabel(module);

  // Re-prime on every open: the dialog is one component reused for every module, and a draft left
  // by the previous request must not leak onto the next.
  useEffect(() => {
    if (!open) return;
    setRole(initialRole);
    setExplanation('');
    create.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- prime on open only
  }, [open, initialRole, module]);

  const send = () => {
    if (module === null) return;
    const trimmed = explanation.trim();
    create.mutate(
      {
        module,
        requested_role: role,
        ...(trimmed.length > 0 ? { explanation: trimmed } : {}),
      },
      {
        onSuccess: (row) => {
          onSent?.(row);
          onOpenChange(false);
        },
      },
    );
  };

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !create.isPending && onOpenChange(next)}>
      <Dialog.Content
        aria-label={`Request access to ${appLabel}`}
        className="flex flex-col gap-16"
        testId={`${PREFIX}-dialog`}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Request access to {appLabel}
        </Dialog.Title>
        <p className="font-sans text-s14 text-text-tertiary" {...testIdProp(`${PREFIX}-lede`)}>
          Choose what you are asking for.
        </p>
        <RadioTileGroup
          value={role}
          onValueChange={setRole}
          aria-label="Choose a role"
          className="gap-12"
          disabled={create.isPending}
          testId={`${PREFIX}-role`}
        >
          <RadioTile
            value={ROLE_MEMBER}
            label={moduleTierLabel(ROLE_MEMBER)}
            description={moduleMemberDescription(appLabel)}
            testId={`${PREFIX}-role-member`}
          />
          <RadioTile
            value={ROLE_OWNER}
            label={moduleTierLabel(ROLE_OWNER)}
            description={moduleOwnerDescription(appLabel)}
            testId={`${PREFIX}-role-owner`}
          />
        </RadioTileGroup>
        <Textarea
          label="Add an explanation? (optional)"
          value={explanation}
          onChange={(event) => setExplanation(event.target.value)}
          placeholder="What you need it for, in a sentence."
          rows={3}
          maxLength={1000}
          disabled={create.isPending}
          testId={`${PREFIX}-explanation`}
        />
        <p
          className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
          {...testIdProp(`${PREFIX}-sentence`)}
        >
          {REQUEST_GOES_TO_SENTENCE}
        </p>
        {create.isError && (
          <ErrorMessage testId={`${PREFIX}-error`}>
            {refusalCopy(create.error, appLabel)}
          </ErrorMessage>
        )}
        <div className="mt-8 flex items-center justify-end gap-8">
          <Dialog.Close
            disabled={create.isPending}
            render={<Button appearance="saas" tone="secondary" size="md" />}
            {...testIdProp(`${PREFIX}-cancel`)}
          >
            Cancel
          </Dialog.Close>
          <Button
            appearance="saas"
            tone="primary"
            size="md"
            loading={create.isPending}
            disabled={module === null}
            onClick={send}
            testId={`${PREFIX}-send`}
          >
            Send
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
